/**
 * Role × band day grid for residential roster coordination.
 *
 * WHY THIS EXISTS. Residential day view previously rendered WorkerStackedGrid
 * (workers down, 24 hours across). On a measured Medea Park day that is ~44
 * shifts across ~42 workers — roughly one chip per row and mostly empty
 * height. The thing a floor coordinator actually manages is cover by role
 * family within the day's natural work bands, not an empty worker column.
 *
 * BANDING IS BY LOCAL START-HOUR RANGE, NOT EXACT START TIMES. An earlier
 * draft claimed three bands at 07:00 / 15:00 / 23:00. Those exact starts cover
 * only 57% of Medea's roster (long tail at 08:00, 09:00, 08:30, 09:30, 06:00…).
 * Ranges cover 100% of measured production starts:
 *
 *   MORNING   local start hour in [06, 14)   67.8%  avg 7.3h
 *   AFTERNOON local start hour in [14, 22)   24.6%  avg 6.2h
 *   NIGHT     local start hour in [22, 24) ∪ [0, 6)  7.7%  avg 8.0h
 *
 * NIGHT wraps midnight: a 23:00 start and a 02:00 start are the same band.
 * Band on the tenant-local hour of the shift start — never UTC, never by
 * splitting an ISO string.
 *
 * ROLE AXIS reuses `classifyRosterService`. `service_type` is uncontrolled free
 * text (`RN`, `RN (registered nurse)`, `Lead RN`, `RN 07.00 - 15.00`…); a second
 * classifier would drift from the board colour map and reintroduce the defect
 * class that produced those variants. Unclassifiable values land in UNKNOWN;
 * missing service lands in UNSET. Neither is dropped — dropping a shift from a
 * coverage grid hides a gap.
 *
 * THIS IS A DATA MODEL ONLY. No React, no DB, no colour. The web surface wires
 * it.
 */

import { classifyRosterService, type RosterServiceFamily } from './roster-service-classification';
import { getHourInTz } from './timezone';

/** Chronological residential day bands. Order is part of the public contract. */
export type RosterDayBand = 'MORNING' | 'AFTERNOON' | 'NIGHT';

/** Always MORNING → AFTERNOON → NIGHT. */
export const ROSTER_DAY_BAND_ORDER: readonly RosterDayBand[] = [
  'MORNING',
  'AFTERNOON',
  'NIGHT',
] as const;

/**
 * Stable role-family axis for the grid. Care roles first, then hotel / facility
 * services, then catch-alls. Every family from `RosterServiceFamily` appears
 * exactly once so the matrix shape never depends on which free-text values
 * happen to show up today.
 */
export const ROSTER_DAY_ROLE_ORDER = [
  'NURSING',
  'CLINICAL',
  'PERSONAL_CARE',
  'CARE_ROUND',
  'ALLIED_HEALTH',
  'SOCIAL_SUPPORT',
  'TRANSPORT',
  'INDEPENDENCE',
  'EVERYDAY_LIVING',
  'CATERING',
  'DOMESTIC',
  'ADMINISTRATION',
  'MAINTENANCE',
  'UNKNOWN',
  'UNSET',
] as const satisfies readonly RosterServiceFamily[];

/** Compile-time guard: a new `RosterServiceFamily` must be placed in the order. */
type _MissingRole = Exclude<RosterServiceFamily, (typeof ROSTER_DAY_ROLE_ORDER)[number]>;
type _AssertRolesComplete = [_MissingRole] extends [never] ? true : never;
const _rolesComplete: _AssertRolesComplete = true;
void _rolesComplete;

export type RosterDayCellCounts = {
  /** Shifts rostered into this cell (assigned + unfilled). */
  readonly rostered: number;
  /** Shifts with a worker assigned. */
  readonly assigned: number;
  /** Shifts with nobody assigned. rostered === assigned + unfilled. */
  readonly unfilled: number;
};

export type RosterDayRoleBandCell = RosterDayCellCounts & {
  readonly band: RosterDayBand;
  readonly role: RosterServiceFamily;
};

export type RosterDayRoleBandGrid = {
  readonly bands: readonly RosterDayBand[];
  readonly roles: readonly RosterServiceFamily[];
  /**
   * Dense matrix: every band × every role has a cell. Empty cells carry zeros
   * rather than being omitted, so a renderer can walk a stable shape.
   */
  readonly cells: ReadonlyArray<ReadonlyArray<RosterDayRoleBandCell>>;
  readonly bandTotals: ReadonlyArray<RosterDayCellCounts & { readonly band: RosterDayBand }>;
  readonly roleTotals: ReadonlyArray<RosterDayCellCounts & { readonly role: RosterServiceFamily }>;
  readonly grandTotal: RosterDayCellCounts;
};

export type RosterDayRoleBandShift = {
  /**
   * Shift start as a UTC instant (`Date` or ISO string). Banded by the local
   * hour of this instant in `timezone`.
   */
  readonly start: Date | string;
  readonly serviceType?: string | null;
  readonly serviceCode?: string | null;
  /** True when a worker is assigned to the shift. */
  readonly isAssigned: boolean;
};

export type BuildRosterDayRoleBandGridInput = {
  /** IANA timezone of the tenant (e.g. `Australia/Hobart`). */
  readonly timezone: string;
  readonly shifts: ReadonlyArray<RosterDayRoleBandShift>;
};

const EMPTY_COUNTS: RosterDayCellCounts = { rostered: 0, assigned: 0, unfilled: 0 };

/**
 * Mutable during accumulation, readonly once returned.
 *
 * The public counts are readonly so a consumer cannot edit a coverage figure in
 * place. The accumulators below genuinely do need to add into them, so they are
 * typed as the mutable twin and widen to the readonly shape on return — rather
 * than the counts being declared mutable, which would let any caller change
 * what the grid reports.
 */
type MutableCellCounts = { -readonly [K in keyof RosterDayCellCounts]: RosterDayCellCounts[K] };

function emptyCounts(): MutableCellCounts {
  return { rostered: 0, assigned: 0, unfilled: 0 };
}

function addCount(
  into: { rostered: number; assigned: number; unfilled: number },
  assigned: boolean,
): void {
  into.rostered += 1;
  if (assigned) into.assigned += 1;
  else into.unfilled += 1;
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Map a local start hour (0–23) to a residential day band.
 *
 * Half-open ranges on the local hour:
 *   MORNING   [6, 14)
 *   AFTERNOON [14, 22)
 *   NIGHT     [22, 24) ∪ [0, 6)
 */
export function rosterDayBandForLocalHour(localHour: number): RosterDayBand {
  if (!Number.isFinite(localHour)) return 'MORNING';
  // Normalise float hours from callers that pass minute-precision decimals.
  const hour = ((Math.floor(localHour) % 24) + 24) % 24;
  if (hour >= 6 && hour < 14) return 'MORNING';
  if (hour >= 14 && hour < 22) return 'AFTERNOON';
  return 'NIGHT';
}

/**
 * Band a shift start instant by tenant-local hour. Invalid / unparseable starts
 * still return a band so the shift is never dropped from the grid; they fall
 * into MORNING as a stable default and are counted — a coverage gap remains
 * visible rather than vanishing.
 */
export function rosterDayBandForStart(start: Date | string, timezone: string): RosterDayBand {
  const date = toDate(start);
  if (Number.isNaN(date.getTime())) return 'MORNING';
  return rosterDayBandForLocalHour(getHourInTz(date, timezone));
}

/**
 * Build the role × band day grid for a residential roster day.
 *
 * Pure: no I/O. Callers filter the day's shifts before calling. Every shift
 * contributes to exactly one cell; unclassifiable service text lands in
 * UNKNOWN / UNSET rather than being omitted.
 */
export function buildRosterDayRoleBandGrid(
  input: BuildRosterDayRoleBandGridInput,
): RosterDayRoleBandGrid {
  const bands = ROSTER_DAY_BAND_ORDER;
  const roles = ROSTER_DAY_ROLE_ORDER;

  type MutableCounts = { rostered: number; assigned: number; unfilled: number };
  const emptyRoleRow = (): Record<RosterServiceFamily, MutableCounts> => {
    const row = {} as Record<RosterServiceFamily, MutableCounts>;
    for (const role of roles) row[role] = emptyCounts();
    return row;
  };
  // Mutable accumulators — frozen into the return shape at the end.
  const matrix: Record<RosterDayBand, Record<RosterServiceFamily, MutableCounts>> = {
    MORNING: emptyRoleRow(),
    AFTERNOON: emptyRoleRow(),
    NIGHT: emptyRoleRow(),
  };

  for (const shift of input.shifts) {
    const band = rosterDayBandForStart(shift.start, input.timezone);
    const role = classifyRosterService({
      serviceCode: shift.serviceCode,
      serviceType: shift.serviceType,
    });
    addCount(matrix[band][role], shift.isAssigned);
  }

  const cells: RosterDayRoleBandCell[][] = bands.map((band) =>
    roles.map((role) => {
      const c = matrix[band][role];
      return {
        band,
        role,
        rostered: c.rostered,
        assigned: c.assigned,
        unfilled: c.unfilled,
      };
    }),
  );

  const bandTotals = bands.map((band) => {
    const total = emptyCounts();
    for (const role of roles) {
      const c = matrix[band][role];
      total.rostered += c.rostered;
      total.assigned += c.assigned;
      total.unfilled += c.unfilled;
    }
    return { band, ...total };
  });

  const roleTotals = roles.map((role) => {
    const total = emptyCounts();
    for (const band of bands) {
      const c = matrix[band][role];
      total.rostered += c.rostered;
      total.assigned += c.assigned;
      total.unfilled += c.unfilled;
    }
    return { role, ...total };
  });

  const grandTotal = emptyCounts();
  for (const b of bandTotals) {
    grandTotal.rostered += b.rostered;
    grandTotal.assigned += b.assigned;
    grandTotal.unfilled += b.unfilled;
  }

  return {
    bands,
    roles,
    cells,
    bandTotals,
    roleTotals,
    grandTotal,
  };
}

/** Lookup a single cell without walking the matrix. Empty when missing. */
export function getRosterDayRoleBandCell(
  grid: RosterDayRoleBandGrid,
  band: RosterDayBand,
  role: RosterServiceFamily,
): RosterDayCellCounts {
  const bandIndex = grid.bands.indexOf(band);
  const roleIndex = grid.roles.indexOf(role);
  if (bandIndex < 0 || roleIndex < 0) return EMPTY_COUNTS;
  const cell = grid.cells[bandIndex]?.[roleIndex];
  if (!cell) return EMPTY_COUNTS;
  return {
    rostered: cell.rostered,
    assigned: cell.assigned,
    unfilled: cell.unfilled,
  };
}
